/**
 * SPAC drill-down grouping: a SPAC strategy type's positions are first grouped
 * by investment_type_code (Unit / Right / Warrant / Equity), then broken out by
 * strategy and investment code underneath.
 */

export const SPAC_STRATEGY_TYPE = "SPAC LIQUIDATION ARBITRAGE";

// Display order for the known SPAC investment types; anything else sorts after.
export const SPAC_INVESTMENT_TYPES = [
  { code: "UN", label: "Unit" },
  { code: "RT", label: "Right" },
  { code: "WT", label: "Warrant" },
  { code: "CS", label: "Equity" },
];

export const isSpacStrategyType = (strategyTypeName) => strategyTypeName === SPAC_STRATEGY_TYPE;

/** Group key for a row's investment type ("" when the investment has none). */
export const investmentTypeKey = (row) => row?.investment_type_code ?? "";

export function investmentTypeLabel(code) {
  if (!code) return "Unclassified";
  const known = SPAC_INVESTMENT_TYPES.find((t) => t.code === code);
  return known ? `${known.label} (${code})` : code;
}

const addVar = (existing, row, field) => {
  if (row[field] != null) existing[field] = (existing[field] ?? 0) + row[field];
};

/** Adds row.greeks ({ scenario: pnl }) into existing.greeks; stays unset if no row has greeks. */
export const addGreeks = (existing, row) => {
  if (!row.greeks) return;
  existing.greeks = existing.greeks || {};
  for (const [scenario, pnl] of Object.entries(row.greeks)) {
    existing.greeks[scenario] = (existing.greeks[scenario] ?? 0) + pnl;
  }
};

/**
 * One row per investment type with exposure and VaR summed. Rows are
 * per-investment_code (as returned by the utilization detail endpoint), already
 * carrying var_95 / var_99 when available. VaR stays null for a type none of
 * whose investments Risk Monitor simulated.
 */
export function aggregateByInvestmentType(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = investmentTypeKey(row);
    const existing = map.get(key) || {
      investment_type_code: key,
      investment_type_label: investmentTypeLabel(key),
      lmv: 0,
      smv: 0,
      gross_exposure: 0,
      position_count: 0,
      var_95: null,
      var_99: null,
    };
    existing.lmv += row.lmv;
    existing.smv += row.smv;
    existing.gross_exposure += row.gross_exposure;
    existing.position_count += 1;
    addVar(existing, row, "var_95");
    addVar(existing, row, "var_99");
    addGreeks(existing, row);
    map.set(key, existing);
  }
  const order = (code) => {
    const i = SPAC_INVESTMENT_TYPES.findIndex((t) => t.code === code);
    return i === -1 ? SPAC_INVESTMENT_TYPES.length : i;
  };
  return [...map.values()].sort((a, b) => order(a.investment_type_code) - order(b.investment_type_code));
}
